if (!customElements.get('monsga-product-reviews')) {
  customElements.define(
    'monsga-product-reviews',
    class extends HTMLElement {
      connectedCallback() {
        if (this.dataset.ready === 'true') return;
        this.dataset.ready = 'true';
        this.filter = 'all';
        this.limit = Number(this.dataset.initialCount || 4);
        this.cards = [...this.querySelectorAll('[data-mpr-card]')];
        this.list = this.querySelector('[data-mpr-list]');
        this.loadMore = this.querySelector('[data-mpr-load-more]');

        this.setupJudgeMe();

        this.querySelectorAll('[data-mpr-filter]').forEach((button) => {
          button.addEventListener('click', () => {
            this.filter = button.dataset.mprFilter;
            this.limit = Number(this.dataset.initialCount || 4);
            this.querySelectorAll('[data-mpr-filter]').forEach((item) => {
              item.setAttribute('aria-pressed', String(item === button));
            });
            this.refresh();
          });
        });

        this.querySelector('[data-mpr-sort]')?.addEventListener('change', (event) => {
          const key = event.currentTarget.value;
          const sorted = [...this.cards].sort((a, b) => {
            if (key === 'helpful') return Number(b.dataset.helpful) - Number(a.dataset.helpful);
            if (key === 'highest') return Number(b.dataset.rating) - Number(a.dataset.rating);
            return Number(b.dataset.order) - Number(a.dataset.order);
          });
          sorted.forEach((card) => this.list?.append(card));
          this.cards = sorted;
          this.refresh();
        });

        this.loadMore?.addEventListener('click', () => {
          this.limit += Number(this.dataset.initialCount || 4);
          this.refresh();
        });

        this.querySelectorAll('[data-mpr-helpful]').forEach((button) => {
          button.addEventListener('click', () => {
            const active = button.getAttribute('aria-pressed') === 'true';
            const count = button.querySelector('[data-mpr-helpful-count]');
            button.setAttribute('aria-pressed', String(!active));
            if (count) count.textContent = String(Math.max(0, Number(count.textContent) + (active ? -1 : 1)));
          });
        });

        this.querySelectorAll('[data-mpr-read-more]').forEach((button) => {
          button.addEventListener('click', () => {
            const card = button.closest('[data-mpr-card]');
            const expanded = card?.dataset.expanded === 'true';
            if (card) card.dataset.expanded = String(!expanded);
            button.textContent = expanded ? button.dataset.moreLabel : button.dataset.lessLabel;
          });
        });

        this.refresh();
      }

      setupJudgeMe() {
        this.appRoot = this.querySelector('.mpr__app');
        if (!this.appRoot) return;

        this.mobileQuery = window.matchMedia('(max-width: 749px)');
        this.enhanceJudgeMe = this.enhanceJudgeMe.bind(this);
        this.scheduleJudgeMeEnhancement = () => {
          if (this.judgeMeFrame) return;
          this.judgeMeFrame = requestAnimationFrame(() => {
            this.judgeMeFrame = null;
            this.enhanceJudgeMe();
          });
        };

        this.appRoot.addEventListener(
          'click',
          (event) => {
            if (!(event.target instanceof Element)) return;
            const photoLink = event.target.closest('.jdgm-rev__pic-link');
            if (!photoLink || !this.appRoot.contains(photoLink)) return;
            event.preventDefault();
            event.stopPropagation();
            this.openLightbox(photoLink);
          },
          true,
        );

        this.mobileQuery.addEventListener?.('change', this.scheduleJudgeMeEnhancement);
        this.judgeMeObserver = new MutationObserver(this.scheduleJudgeMeEnhancement);
        this.judgeMeObserver.observe(this.appRoot, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class', 'data-src', 'src'],
        });
        this.scheduleJudgeMeEnhancement();
        // Judge.me adds a temporary hiding style while it hydrates. On this
        // storefront the review nodes can already be present when that style
        // is left behind, which makes the list flash and then disappear.
        this.judgeMeRevealTimer = window.setTimeout(() => this.revealJudgeMeBody(), 1800);
      }

      disconnectedCallback() {
        this.judgeMeObserver?.disconnect();
        this.mobileQuery?.removeEventListener?.('change', this.scheduleJudgeMeEnhancement);
        if (this.judgeMeFrame) cancelAnimationFrame(this.judgeMeFrame);
        if (this.judgeMeRevealTimer) window.clearTimeout(this.judgeMeRevealTimer);
        this.closeLightbox();
        this.lightbox?.remove();
        this.lightbox = null;
      }

      enhanceJudgeMe() {
        const versionThreeWidget = this.appRoot.querySelector('.jm-review-widget');
        if (versionThreeWidget) {
          this.enhanceJudgeMeVersionThree(versionThreeWidget);
          return;
        }

        this.translateJudgeMeChrome();
        this.renderFeaturedReviews();
        this.arrangeLegacyControls();
        this.revealJudgeMeBody();

        const summaryStars = this.appRoot.querySelector('.jdgm-rev-widg__summary-stars');
        if (summaryStars) {
          const rating = Number(summaryStars.getAttribute('aria-label')?.match(/[\d.]+/)?.[0] || 0);
          if (rating && !summaryStars.previousElementSibling?.classList.contains('mpr-judge__average')) {
            const average = document.createElement('strong');
            average.className = 'mpr-judge__average';
            average.textContent = rating.toFixed(1);
            summaryStars.before(average);
          }
          summaryStars.textContent = '★★★★★';
        }

        this.appRoot.querySelectorAll('.jdgm-rev__pic-img[data-src]').forEach((image) => {
          const source = image.dataset.src?.trim();
          if (!source) return;
          if (!image.getAttribute('src')) image.src = source;
          image.loading = 'lazy';
          image.decoding = 'async';
          const link = image.closest('.jdgm-rev__pic-link');
          const finish = () => link?.classList.remove('jdgm--loading');
          if (image.complete) finish();
          if (image.dataset.mprImageReady !== 'true') {
            image.dataset.mprImageReady = 'true';
            image.addEventListener('load', finish, { once: true });
            image.addEventListener('error', finish, { once: true });
          }
        });

        this.appRoot.querySelectorAll('.jdgm-rev').forEach((review) => {
          const rating = review.querySelector('.jdgm-rev__rating');
          const score = Number(rating?.dataset.score || 0);
          if (rating && score) rating.textContent = `${'★'.repeat(score)}${'☆'.repeat(Math.max(0, 5 - score))}`;

          const timestamp = review.querySelector('.jdgm-rev__timestamp[datetime]');
          if (timestamp && !timestamp.textContent.trim()) {
            const date = new Date(timestamp.dateTime);
            if (!Number.isNaN(date.valueOf())) {
              timestamp.textContent = date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              });
              timestamp.classList.remove('jdgm-spinner');
            }
          }

          const author = review.querySelector('.jdgm-rev__author');
          if (author) {
            const suppliedName = author.textContent.trim();
            const name = !suppliedName || /^[-\u2013\u2014\s]+$/.test(suppliedName) ? 'Amazon Customer' : suppliedName;
            if (author.textContent.trim() !== name) author.textContent = name;
            const avatar = review.querySelector('.jdgm-rev__icon');
            if (avatar) {
              const initials = name
                .split(/\s+/)
                .filter(Boolean)
                .slice(0, 2)
                .map((part) => part[0])
                .join('')
                .toUpperCase();
              avatar.textContent = initials || 'A';
              avatar.setAttribute('aria-hidden', 'true');
            }
          }

          this.enhanceReviewBody(review);
        });

        const sort = this.appRoot.querySelector('.jdgm-sort-dropdown');
        if (sort && !['complete', 'pending'].includes(this.dataset.mprDefaultSort)) {
          this.dataset.mprDefaultSort = 'pending';
          window.setTimeout(() => {
            const currentSort = this.appRoot?.querySelector('.jdgm-sort-dropdown');
            const body = this.appRoot?.querySelector('.jdgm-rev-widg__body');
            const picturesFirst = currentSort && [...currentSort.options].find((option) => {
              const value = `${option.value} ${option.textContent}`.toLowerCase();
              return value.includes('pictures') && value.includes('first');
            });
            if (currentSort && picturesFirst && currentSort.value !== picturesFirst.value && body && getComputedStyle(body).display !== 'none') {
              currentSort.value = picturesFirst.value;
              currentSort.dispatchEvent(new Event('change', { bubbles: true }));
            }
            this.dataset.mprDefaultSort = 'complete';
            this.revealJudgeMeBody();
          }, 350);
        }
      }

      revealJudgeMeBody() {
        const widget = this.appRoot?.querySelector('.jdgm-rev-widg');
        if (!widget) return;
        const body = widget.querySelector('.jdgm-rev-widg__body');
        const reviews = widget.querySelector('.jdgm-rev-widg__reviews');
        const hasReviews = Boolean(reviews?.querySelector('.jdgm-rev'));
        const hasFeatured = Boolean(this.appRoot.querySelector('.mpr-featured__card'));
        if (!body || (!hasReviews && !hasFeatured)) return;

        widget.querySelectorAll('style.jdgm-temp-hiding-style').forEach((style) => style.remove());
        widget.style.removeProperty('display');
        body.style.removeProperty('display');
      }

      translateJudgeMeChrome() {
        const language = (document.documentElement.lang || location.pathname.split('/')[1] || 'en').toLowerCase().split('-')[0];
        const copy = {
          de: { based: 'Basierend auf $1 Bewertungen', newest: 'Neueste', helpful: 'Am hilfreichsten', highest: 'Höchste Bewertung', all: 'Alle Bewertungen', media: 'Mit Medien', load: 'Weitere Bewertungen laden' },
          fr: { based: 'Basé sur $1 avis', newest: 'Les plus récents', helpful: 'Les plus utiles', highest: 'Note la plus élevée', all: 'Tous les avis', media: 'Avec médias', load: 'Charger plus d’avis' },
          it: { based: 'Basato su $1 recensioni', newest: 'Più recenti', helpful: 'Più utili', highest: 'Valutazione più alta', all: 'Tutte le recensioni', media: 'Con contenuti multimediali', load: 'Carica altre recensioni' },
        }[language];
        if (!copy) return;

        const summary = this.appRoot.querySelector('.jdgm-rev-widg__summary-text');
        if (summary) {
          const match = summary.textContent.trim().match(/([\d,.]+)\s*reviews?/i);
          if (match) summary.textContent = copy.based.replace('$1', match[1]);
        }

        const sort = this.appRoot.querySelector('.jdgm-sort-dropdown');
        if (sort) {
          [...sort.options].forEach((option) => {
            const value = `${option.value} ${option.textContent}`.toLowerCase();
            if (value.includes('newest') || value.includes('recent')) option.textContent = copy.newest;
            else if (value.includes('helpful')) option.textContent = copy.helpful;
            else if (value.includes('highest') || value.includes('rating')) option.textContent = copy.highest;
          });
        }

        this.appRoot.querySelectorAll('.jdgm-rev-widg__summary-stars').forEach((stars) => {
          stars.setAttribute('aria-label', stars.getAttribute('aria-label')?.replace(/out of 5 stars/i, language === 'de' ? 'von 5 Sternen' : language === 'fr' ? 'sur 5 étoiles' : 'su 5 stelle') || '');
        });
      }

      arrangeLegacyControls() {
        const reviews = this.appRoot.querySelector('.jdgm-rev-widg__reviews');
        const reviewsParent = reviews?.parentElement;
        const actions = this.appRoot.querySelector('.jdgm-widget-actions-wrapper');
        const sort = this.appRoot.querySelector('.jdgm-rev-widg__sort-wrapper');
        if (!reviews || !reviewsParent || (!actions && !sort)) return;

        let toolbar = reviewsParent.querySelector(':scope > .mpr-judge__toolbar');
        if (!toolbar) {
          toolbar = document.createElement('div');
          toolbar.className = 'mpr-judge__toolbar';
          toolbar.setAttribute('aria-label', 'Review controls');
        }

        if (sort && sort.parentElement !== toolbar) toolbar.append(sort);
        if (actions && actions.parentElement !== toolbar) toolbar.append(actions);
        if (toolbar.parentElement !== reviewsParent || toolbar.nextElementSibling !== reviews) {
          reviewsParent.insertBefore(toolbar, reviews);
        }
      }

      enhanceJudgeMeVersionThree(widget) {
        widget.classList.add('mpr-judge-v3');

        const average = widget.querySelector('.jm-average-rating-display .jm-cluster');
        const totalText = average?.lastElementChild?.textContent || '';
        const totalReviews = Number(totalText.match(/[\d,]+/)?.[0].replace(/,/g, ''));
        const reviewData = Object.values(window.jdgm?.data?.reviewWidget || {}).find(
          (entry) => Array.isArray(entry?.histogram) && Number(entry.number_of_reviews) === totalReviews
        ) || Object.values(window.jdgm?.data?.reviewWidget || {}).find((entry) => Array.isArray(entry?.histogram));
        const rating = Number(reviewData?.average_rating || average?.firstElementChild?.textContent || 0);

        if (average) {
          let stars = average.querySelector('.mpr-judge-v3__summary-stars');
          if (!stars) {
            stars = document.createElement('span');
            stars.className = 'mpr-judge-v3__summary-stars';
            stars.textContent = '★★★★★';
            average.insertBefore(stars, average.lastElementChild);
          }
          stars.setAttribute('aria-label', `${rating.toFixed(2)} out of 5 stars`);
        }

        const headerLayout = widget.querySelector('.jm-review-widget__header > .jm-cluster');
        const filters = widget.querySelector('.jm-review-widget-minimal-header__filter-container');
        if (headerLayout && filters && reviewData?.histogram) {
          const distributionSignature = JSON.stringify([
            reviewData.number_of_reviews,
            reviewData.average_rating,
            reviewData.histogram,
          ]);
          let distribution = headerLayout.querySelector('.mpr-judge-v3__distribution');
          if (!distribution) {
            distribution = document.createElement('div');
            distribution.className = 'mpr-judge-v3__distribution';
            distribution.setAttribute('role', 'list');
            headerLayout.insertBefore(distribution, filters);
          }

          if (distribution.dataset.mprSignature !== distributionSignature) {
            const title = document.createElement('strong');
            title.className = 'mpr-judge-v3__distribution-title';
            title.textContent = 'Rating breakdown';
            const rows = [...reviewData.histogram]
              .sort((a, b) => Number(b.rating) - Number(a.rating))
              .map((item) => {
                const row = document.createElement('div');
                const percentage = Math.max(0, Math.min(100, Number(item.percentage) || 0));
                const frequency = Number(item.frequency) || 0;
                row.className = 'mpr-judge-v3__distribution-row';
                row.setAttribute('role', 'listitem');
                row.setAttribute('aria-label', `${item.rating} stars, ${frequency} reviews, ${percentage} percent`);

                const label = document.createElement('span');
                label.className = 'mpr-judge-v3__distribution-label';
                label.textContent = `${item.rating} ★`;
                const track = document.createElement('span');
                track.className = 'mpr-judge-v3__distribution-track';
                track.setAttribute('aria-hidden', 'true');
                const fill = document.createElement('span');
                fill.className = 'mpr-judge-v3__distribution-fill';
                fill.style.setProperty('--mpr-rating-fill', `${percentage}%`);
                track.append(fill);
                const value = document.createElement('span');
                value.className = 'mpr-judge-v3__distribution-value';
                value.textContent = `${percentage}%`;
                const count = document.createElement('span');
                count.className = 'mpr-judge-v3__distribution-count';
                count.textContent = `(${frequency})`;
                row.append(label, track, value, count);
                return row;
              });
            distribution.replaceChildren(title, ...rows);
            distribution.dataset.mprSignature = distributionSignature;
          }
        }

        const media = widget.querySelector('.jm-media-preview');
        if (media) {
          media.id = 'reviews-media';
          if (!media.previousElementSibling?.classList.contains('mpr-judge-v3__media-heading')) {
            const heading = document.createElement('div');
            heading.className = 'mpr-judge-v3__media-heading';
            const headingTitle = document.createElement('strong');
            headingTitle.textContent = this.dataset.photosTitle || 'Top-rated customer photos';
            const handle = (window.location.pathname.split('/products/')[1] || '').toLowerCase();
            const model = handle.includes('mr7') ? 'MR7 PRO' : handle.includes('mv3') ? 'MV3' : handle.includes('mr1') ? 'MR1' : '';
            const headingNote = document.createElement('span');
            const noteTemplate = this.dataset.photosNote || 'Real {model} homes';
            headingNote.textContent = model ? noteTemplate.replace('{model}', model) : 'Real customer homes';
            heading.append(headingTitle, headingNote);
            media.before(heading);
          }

          const viewMore = this.querySelector('.mpr__view-more');
          if (viewMore) {
            viewMore.textContent = 'See customer photos';
            viewMore.href = '#reviews-media';
            if (viewMore.dataset.mprFeaturedLink !== 'true') {
              viewMore.dataset.mprFeaturedLink = 'true';
              viewMore.addEventListener('click', (event) => {
                event.preventDefault();
                this.querySelector('#reviews-media')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
              });
            }
          }
        }

        widget.querySelectorAll('.jm-reviewer-info__name').forEach((author) => {
          const suppliedName = author.textContent.trim();
          if (suppliedName && !/^[-\u2013\u2014\s]+$/.test(suppliedName)) return;
          author.textContent = 'Amazon Customer';
          author.title = 'Amazon Customer';
          const initial = author.closest('.jm-reviewer-info')?.querySelector('.jm-reviewer-avatar__initial .jm-text');
          if (initial) initial.textContent = 'A';
        });

        const sort = widget.querySelector('.jdgm-sort-dropdown');
        if (sort && !['complete', 'pending'].includes(this.dataset.mprDefaultSort)) {
          this.dataset.mprDefaultSort = 'pending';
          window.setTimeout(() => {
            const currentSort = widget.querySelector('.jdgm-sort-dropdown');
            const picturesFirst = currentSort && [...currentSort.options].find((option) => {
              const value = `${option.value} ${option.textContent}`.toLowerCase();
              return value.includes('pictures') && value.includes('first');
            });
            if (currentSort && picturesFirst && currentSort.value === 'created_at') {
              currentSort.value = picturesFirst.value;
              currentSort.dispatchEvent(new Event('change', { bubbles: true }));
            }
            this.dataset.mprDefaultSort = 'complete';
            this.revealJudgeMeBody();
          }, 350);
        }
      }

      renderFeaturedReviews() {
        const galleryData = this.appRoot.querySelector('.jdgm-gallery-data');
        const widget = this.appRoot.querySelector('.jdgm-rev-widg');
        const reviews = this.appRoot.querySelector('.jdgm-rev-widg__reviews');
        if (!widget || !reviews) return;

        let items = [];
        if (galleryData) {
          try {
            items = JSON.parse(galleryData.dataset.json || '[]');
          } catch (_error) {
            items = [];
          }
          if (items.length) this.galleryItems = items;
        } else if (this.galleryItems?.length) {
          items = this.galleryItems;
        }

        // Judge.me removes its gallery-data node after hydration on some
        // storefronts. Rebuild the small featured set from the rendered
        // review cards so the presentation remains stable after that pass.
        if (!items.length) {
          items = [...reviews.querySelectorAll('.jdgm-rev')].map((review) => {
            const reviewerName = review.querySelector('.jdgm-rev__author')?.textContent.trim() || '';
            const title = review.querySelector('.jdgm-rev__title')?.textContent.trim() || '';
            const body = review.querySelector('.jdgm-rev__body')?.innerHTML || '';
            const rating = review.querySelector('.jdgm-rev__rating')?.dataset.score || '';
            const pictures_urls = [...review.querySelectorAll('.jdgm-rev__pic-link')].map((link) => {
              const image = link.querySelector('img');
              const compact = image?.currentSrc || image?.src || image?.dataset.src || '';
              const original = link.href || compact;
              return compact ? { compact, original } : null;
            }).filter(Boolean);
            return { uuid: review.dataset.reviewId || `${reviewerName}-${title}`, reviewer_name: reviewerName, title, body_html: body, rating, pictures_urls };
          });
        }

        const featured = items
          .filter((item) => Number(item.rating) === 5 && item.pictures_urls?.length)
          .slice(0, 4);
        if (!featured.length) return;

        let section = this.appRoot.querySelector('.mpr-featured');
        if (!section) {
          section = document.createElement('section');
          section.className = 'mpr-featured';
          section.id = 'reviews-media';
          section.setAttribute('aria-labelledby', 'reviews-media-title');
          section.innerHTML = `
            <div class="mpr-featured__heading">
              <div>
                <span class="mpr-featured__eyebrow">Customer photos</span>
                <h3 id="reviews-media-title">Top-rated in real homes</h3>
              </div>
              <span class="mpr-featured__count"></span>
            </div>
            <div class="mpr-featured__grid"></div>`;
        }

        if (section.dataset.signature !== featured.map((item) => item.uuid).join(',')) {
          const grid = section.querySelector('.mpr-featured__grid');
          grid.replaceChildren();
          featured.forEach((item) => {
            const article = document.createElement('article');
            article.className = 'mpr-featured__card';

            const mediaLink = document.createElement('a');
            mediaLink.className = 'jdgm-rev__pic-link mpr-featured__media';
            mediaLink.href = item.pictures_urls[0].original;
            mediaLink.setAttribute('aria-label', `Open ${item.reviewer_name || 'customer'} review photo`);

            const image = document.createElement('img');
            image.className = 'mpr-featured__image';
            image.src = item.pictures_urls[0].compact || item.pictures_urls[0].original;
            image.dataset.original = item.pictures_urls[0].original;
            image.loading = 'lazy';
            image.decoding = 'async';
            image.alt = `${item.reviewer_name || 'Customer'} review photo`;
            mediaLink.append(image);

            if (item.pictures_urls.length > 1) {
              const mediaCount = document.createElement('span');
              mediaCount.className = 'mpr-featured__media-count';
              mediaCount.textContent = `+${item.pictures_urls.length - 1}`;
              mediaLink.append(mediaCount);
              item.pictures_urls.slice(1).forEach((picture, index) => {
                const hiddenLink = document.createElement('a');
                hiddenLink.className = 'jdgm-rev__pic-link mpr-featured__extra-media';
                hiddenLink.href = picture.original;
                hiddenLink.tabIndex = -1;
                hiddenLink.setAttribute('aria-hidden', 'true');
                const hiddenImage = document.createElement('img');
                hiddenImage.src = picture.compact || picture.original;
                hiddenImage.dataset.original = picture.original;
                hiddenImage.alt = `${item.reviewer_name || 'Customer'} review photo ${index + 2}`;
                hiddenLink.append(hiddenImage);
                article.append(hiddenLink);
              });
            }

            const content = document.createElement('div');
            content.className = 'mpr-featured__content';
            const stars = document.createElement('span');
            stars.className = 'mpr-featured__stars';
            stars.setAttribute('aria-label', '5 out of 5 stars');
            stars.textContent = '★★★★★';
            const title = document.createElement('h4');
            title.textContent = item.title || 'Great purchase';
            const body = document.createElement('p');
            const parsedBody = new DOMParser().parseFromString(item.body_html || '', 'text/html');
            body.textContent = parsedBody.body.textContent?.trim() || '';
            const author = document.createElement('span');
            author.className = 'mpr-featured__author';
            author.textContent = item.reviewer_name || 'Amazon Customer';
            content.append(stars, title, body, author);
            article.prepend(mediaLink);
            article.append(content);
            grid.append(article);
          });
          section.querySelector('.mpr-featured__count').textContent = `${featured.length} featured reviews`;
          section.dataset.signature = featured.map((item) => item.uuid).join(',');
        }

        // Judge.me can render the review list inside a nested wrapper while it
        // hydrates. Insert beside the list's actual parent instead of assuming
        // the outer widget owns it; the old assumption threw NotFoundError and
        // stopped the rest of the enhancement pass (filters, sort and media).
        const reviewsParent = reviews.parentElement;
        const toolbar = reviewsParent?.querySelector(':scope > .mpr-judge__toolbar');
        const anchor = toolbar || reviews;
        if (reviewsParent && (section.parentElement !== reviewsParent || section.nextElementSibling !== anchor)) {
          reviewsParent.insertBefore(section, anchor);
        }
        const viewMore = this.querySelector('.mpr__view-more');
        if (viewMore) {
          viewMore.textContent = 'See customer photos';
          viewMore.href = '#reviews-media';
          if (viewMore.dataset.mprFeaturedLink !== 'true') {
            viewMore.dataset.mprFeaturedLink = 'true';
            viewMore.addEventListener('click', (event) => {
              event.preventDefault();
              this.querySelector('#reviews-media')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            });
          }
        }
      }

      enhanceReviewBody(review) {
        const body = review.querySelector('.jdgm-rev__body');
        if (!body) return;

        let button = review.querySelector('.mpr-judge__read-more');
        if (body.dataset.expanded !== 'true') {
          body.dataset.expanded = 'false';
          body.classList.add('mpr-judge__body--collapsed');
        }

        if (!button) {
          button = document.createElement('button');
          button.className = 'mpr-judge__read-more';
          button.type = 'button';
          button.textContent = this.dataset.moreLabel || 'Read more';
          button.setAttribute('aria-expanded', 'false');
          button.addEventListener('click', () => {
            const expanded = body.dataset.expanded === 'true';
            body.dataset.expanded = String(!expanded);
            body.classList.toggle('mpr-judge__body--collapsed', expanded);
            button.textContent = expanded ? (this.dataset.moreLabel || 'Read more') : (this.dataset.lessLabel || 'Show less');
            button.setAttribute('aria-expanded', String(!expanded));
          });
          body.insertAdjacentElement('afterend', button);
        }

        button.hidden = body.scrollHeight <= body.clientHeight + 2 && body.dataset.expanded !== 'true';
      }

      ensureLightbox() {
        if (this.lightbox) return;
        const lightbox = document.createElement('div');
        lightbox.className = 'mpr-lightbox';
        lightbox.hidden = true;
        lightbox.setAttribute('role', 'dialog');
        lightbox.setAttribute('aria-modal', 'true');
        lightbox.setAttribute('aria-label', 'Customer review photo');
        lightbox.innerHTML = `
          <button class="mpr-lightbox__backdrop" type="button" aria-label="Close image preview"></button>
          <div class="mpr-lightbox__stage">
            <button class="mpr-lightbox__close" type="button" aria-label="Close image preview">&times;</button>
            <button class="mpr-lightbox__nav mpr-lightbox__nav--previous" type="button" aria-label="Previous photo">&#8249;</button>
            <img class="mpr-lightbox__image" alt="Customer review photo">
            <button class="mpr-lightbox__nav mpr-lightbox__nav--next" type="button" aria-label="Next photo">&#8250;</button>
            <span class="mpr-lightbox__count" aria-live="polite"></span>
          </div>`;
        document.body.append(lightbox);
        lightbox.querySelector('.mpr-lightbox__backdrop').addEventListener('click', () => this.closeLightbox());
        lightbox.querySelector('.mpr-lightbox__close').addEventListener('click', () => this.closeLightbox());
        lightbox.querySelector('.mpr-lightbox__nav--previous').addEventListener('click', () => this.showLightboxImage(this.lightboxIndex - 1));
        lightbox.querySelector('.mpr-lightbox__nav--next').addEventListener('click', () => this.showLightboxImage(this.lightboxIndex + 1));
        lightbox.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') this.closeLightbox();
          if (event.key === 'ArrowLeft') this.showLightboxImage(this.lightboxIndex - 1);
          if (event.key === 'ArrowRight') this.showLightboxImage(this.lightboxIndex + 1);
        });
        this.lightbox = lightbox;
      }

      openLightbox(selectedLink) {
        this.ensureLightbox();
        const review = selectedLink.closest('.jdgm-rev, .mpr-featured__card');
        const links = [...(review || this.appRoot).querySelectorAll('.jdgm-rev__pic-link')];
        this.lightboxImages = links
          .map((link) => {
            const image = link.querySelector('img');
            return {
              url: image?.dataset.original || link.href || image?.currentSrc || image?.src || image?.dataset.src,
              alt: image?.alt || 'Customer review photo',
            };
          })
          .filter((item) => item.url);
        this.lightboxIndex = Math.max(0, links.indexOf(selectedLink));
        this.lightboxReturnFocus = selectedLink;
        this.previousBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        this.lightbox.hidden = false;
        this.showLightboxImage(this.lightboxIndex);
        this.lightbox.querySelector('.mpr-lightbox__close').focus();
      }

      showLightboxImage(index) {
        if (!this.lightboxImages?.length) return;
        this.lightboxIndex = (index + this.lightboxImages.length) % this.lightboxImages.length;
        const current = this.lightboxImages[this.lightboxIndex];
        const image = this.lightbox.querySelector('.mpr-lightbox__image');
        image.src = current.url;
        image.alt = current.alt;
        const multiple = this.lightboxImages.length > 1;
        this.lightbox.querySelectorAll('.mpr-lightbox__nav').forEach((button) => {
          button.hidden = !multiple;
        });
        this.lightbox.querySelector('.mpr-lightbox__count').textContent = multiple
          ? `${this.lightboxIndex + 1} / ${this.lightboxImages.length}`
          : '';
      }

      closeLightbox() {
        if (!this.lightbox || this.lightbox.hidden) return;
        this.lightbox.hidden = true;
        document.body.style.overflow = this.previousBodyOverflow || '';
        this.lightboxReturnFocus?.focus();
      }

      matches(card) {
        if (this.filter === 'all') return true;
        if (this.filter === 'media') return card.dataset.media === 'true';
        return card.dataset.rating === this.filter;
      }

      refresh() {
        let visible = 0;
        const matches = this.cards.filter((card) => this.matches(card));
        this.cards.forEach((card) => {
          const show = this.matches(card) && visible < this.limit;
          card.hidden = !show;
          if (show) visible += 1;
        });
        if (this.loadMore) this.loadMore.hidden = visible >= matches.length;
      }
    },
  );
}
